import React from 'react';
import { motion } from 'framer-motion';
import {MapPin, Train, DollarSign, Clock, Utensils, Building2} from 'lucide-react';
import { useScrollAnimation, useParallax } from '../../hooks/useScrollAnimation';

export default function Tokyo() {
  const scrollY = useParallax();
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation(0.2);
  const { ref: neighborhoodsRef, isVisible: neighborhoodsVisible } = useScrollAnimation(0.2);
  const { ref: attractionsRef, isVisible: attractionsVisible } = useScrollAnimation(0.2);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Hero with Parallax */}
      <div className="relative h-[400px] overflow-hidden">
        <motion.img
          src="https://images.pexels.com/photos/2506923/pexels-photo-2506923.jpeg?auto=compress&cs=tinysrgb&w=1600"
          alt="Tokyo"
          className="w-full h-full object-cover"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/80 to-transparent" />
        <motion.div 
          className="absolute inset-0 flex items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h1 
              className="text-5xl font-bold text-white mb-2"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Tokyo
            </motion.h1>
            <motion.p 
              className="text-xl text-white/90"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              The world's most vibrant metropolis
            </motion.p>
          </div>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Quick Info Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {[
            { icon: Clock, color: '#4A90E2', label: 'Recommended Stay', value: '4-6 days' },
            { icon: DollarSign, color: '#F7B32B', label: 'Daily Budget', value: '¥8,000-15,000' },
            { icon: Train, color: '#FF6B35', label: 'Best Transport', value: 'JR Yamanote Line' },
            { icon: Utensils, color: '#0A2540', label: 'Must-Try Food', value: 'Sushi, Ramen' }
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="bg-white rounded-lg p-4 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              whileHover={{ y: -5, boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
            >
              <item.icon className="w-6 h-6 mb-2" style={{ color: item.color }} />
              <p className="text-sm text-gray-500">{item.label}</p>
              <p className="font-semibold text-gray-800">{item.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <motion.div
              ref={contentRef}
              initial={{ opacity: 0, x: -30 }}
              animate={contentVisible ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold text-[#0A2540] mb-4">About Tokyo</h2>
              <p className="text-gray-600 mb-4">
                Tokyo is a city of contrasts where ancient temples sit beside towering skyscrapers, and traditional tea ceremonies are moments away from cutting-edge technology. With 13 million residents, it's energetic yet orderly, chaotic yet punctual.
              </p>
              <p className="text-gray-600 mb-8">
                Explore the electric streets of Shibuya, browse manga in Akihabara, find serenity at Senso-ji Temple, and savor world-class cuisine from Michelin-starred restaurants to tiny ramen shops.
              </p>
            </motion.div>

            <div ref={neighborhoodsRef}>
              <motion.h3 
                className="text-2xl font-bold text-[#0A2540] mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={neighborhoodsVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5 }}
              >
                Top Neighborhoods
              </motion.h3>
              <div className="space-y-4 mb-8">
                {[
                  { name: 'Shibuya', desc: 'Famous crossing, youth culture, shopping' },
                  { name: 'Shinjuku', desc: 'Business district, nightlife, observation decks' },
                  { name: 'Asakusa', desc: 'Historic temples, traditional atmosphere' },
                  { name: 'Harajuku', desc: 'Fashion, trendy cafes, Meiji Shrine' },
                  { name: 'Akihabara', desc: 'Electronics, anime, gaming culture' }
                ].map((area, idx) => (
                  <motion.div 
                    key={idx} 
                    className="bg-white rounded-lg p-4 shadow-sm border-l-4 border-[#FF6B35]"
                    initial={{ opacity: 0, x: -30 }}
                    animate={neighborhoodsVisible ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                    whileHover={{ x: 5, boxShadow: '0 8px 20px rgba(0,0,0,0.1)' }}
                  >
                    <h4 className="font-semibold text-gray-800 mb-1">{area.name}</h4>
                    <p className="text-sm text-gray-600">{area.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            <div ref={attractionsRef}>
              <motion.h3 
                className="text-2xl font-bold text-[#0A2540] mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={attractionsVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5 }}
              >
                Must-See Attractions
              </motion.h3>
              <ul className="space-y-2 text-gray-700">
                {[
                  { name: 'Tokyo Skytree', desc: '634m observation tower with stunning city views' },
                  { name: 'Senso-ji Temple', desc: 'Tokyo\'s oldest Buddhist temple in Asakusa' },
                  { name: 'Meiji Shrine', desc: 'Peaceful Shinto shrine in forest setting' },
                  { name: 'Tsukiji Outer Market', desc: 'Fresh seafood and street food' }
                ].map((attr, idx) => (
                  <motion.li 
                    key={idx}
                    className="flex items-start"
                    initial={{ opacity: 0, x: -20 }}
                    animate={attractionsVisible ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                  >
                    <MapPin className="w-5 h-5 text-[#4A90E2] mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>{attr.name}:</strong> {attr.desc}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>

          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <motion.div 
              className="bg-white rounded-xl p-6 shadow-lg mb-6"
              whileHover={{ y: -5, boxShadow: '0 15px 35px rgba(0,0,0,0.1)' }}
            >
              <h3 className="text-xl font-bold text-[#0A2540] mb-4">Quick Tips</h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="text-[#4A90E2] mr-2">✓</span>
                  <span>Get a Suica/Pasmo card for easy train travel</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A90E2] mr-2">✓</span>
                  <span>Many restaurants close between lunch and dinner</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A90E2] mr-2">✓</span>
                  <span>Cash is still king – carry yen</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#4A90E2] mr-2">✓</span>
                  <span>Respect quiet zones on trains</span>
                </li>
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
